import InfoIcon from '@mui/icons-material/Info';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';

import { FORTA_GRAY4, FORTA_BLUE, FORTA_WHITE } from '../../../../../../../utils/colors';
import { formatSecondsToHHMM, getUSTimeByZipcode } from '../../../../../../../helpers';
import { CT_TIMEZONE_AREA } from '../../../../../../../constants';
import { FHTooltip, FBoxContainer } from '../../../../../../../components/base';
import { BAYS } from '../../../../../../../constants';
import {
  formatSessionDateTime,
  getSessionProviderRoleLabelTranslationKey,
} from '../../utils/general';

import type { IBTSession } from '../../../../../../../utils/types';

interface IHeaderContentReadOnlyProps {
  session: IBTSession;
}

function HeaderContentReadOnly({ session }: IHeaderContentReadOnlyProps) {
  const { t } = useTranslation('sessionNotes');
  const [clientTimeAndZoneByZipcode, , isTZIdentified] = session
    ? getUSTimeByZipcode(session.date_start, session.client?.street_address_zip)
    : [moment(), CT_TIMEZONE_AREA, false];

  return (
    <FBoxContainer sx={{ borderRadius: '8px', boxShadow: 'none', border: 'none' }}>
      <Grid
        container
        alignItems="center"
        justifyContent="flex-start"
        color={FORTA_GRAY4}
        fontSize={16}
        textAlign="left"
        spacing={1}
      >
        <Grid item lg={3} md={4} sm={6} xs={12} mb={0.5}>
          <Typography fontWeight="bold">{t('client')}</Typography>
          <Typography>{`${session.client?.first_name} ${session.client?.last_name}`}</Typography>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12} mb={0.5}>
          <Typography fontWeight="bold">
            {t(
              getSessionProviderRoleLabelTranslationKey(session.bay, session.clinical_relationship),
            )}
          </Typography>
          <Typography>
            {`${session.session_provider?.first_name} ${session.session_provider?.last_name}`}
          </Typography>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12} mb={0.5}>
          <Typography fontWeight="bold">{t('startDateTime')}</Typography>
          <Typography>{formatSessionDateTime(session.date_start)}</Typography>
        </Grid>

        {isTZIdentified && (
          <Grid item lg={3} md={4} sm={6} xs={12} mb={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography fontWeight="bold">{t('clientStartDateTime')}</Typography>
              <FHTooltip
                sx={{ inset: 'auto auto -6px 0px' }}
                title={`${t('clientTimezone')} (${t('clientZipCode')}: ${
                  session?.client?.street_address_zip
                })`}
                arrow
                placement="top"
                maxwidth={340}
                tooltipBorderRadius="8px"
                tooltipbackground={FORTA_WHITE}
                tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
              >
                <Box sx={{ ml: 0.75, mt: 0.5 }}>
                  <InfoIcon sx={{ color: FORTA_BLUE.primary, width: 21, height: 21 }} />
                </Box>
              </FHTooltip>
            </Box>
            <Typography display="inline">
              {formatSessionDateTime(clientTimeAndZoneByZipcode)}
            </Typography>
            {isTZIdentified && (
              <Typography display="inline" ml={0.5}>{`(${(
                clientTimeAndZoneByZipcode
              ).format('z')})`}</Typography>
            )}
          </Grid>
        )}
        <Grid item lg={3} md={4} sm={6} xs={12} mb={0.5}>
          <Typography fontWeight="bold">{t('duration')}</Typography>
          <Typography color={FORTA_GRAY4}>{`${formatSecondsToHHMM(
            session.expected_duration,
          )}`}</Typography>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12} mb={0.5}>
          <Typography fontWeight="bold">
            {session.bay === BAYS.BAY_6
              ? t('supervisingBCBAPresentBay6')
              : t('supervisingBCBAPresent')}
          </Typography>
          <Typography color={FORTA_GRAY4}>{session.is_supervised ? 'Yes' : 'No'}</Typography>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12} mb={0.5}>
          <Typography fontWeight="bold">{t('clientLocation')}</Typography>
          <Typography color={FORTA_GRAY4}>{session.location}</Typography>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12} mb={0.5}>
          <Typography fontWeight="bold">{t('deliveryMethod')}</Typography>
          <Typography color={FORTA_GRAY4}>{session.delivery_method}</Typography>
        </Grid>
      </Grid>
    </FBoxContainer>
  );
}

export default HeaderContentReadOnly;
